import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { User, Mail, Phone, Save, Key, MapPin } from 'lucide-react';
import { authAPI } from '@shared/apiClient';
import { useAuth } from '@shared/authContext';
import toast from 'react-hot-toast';

export default function Profile() {
  const { user, updateUser } = useAuth();
  const queryClient = useQueryClient();
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    watch: watchProfile,
    formState: { errors: profileErrors },
  } = useForm({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
      homeAddress: user?.homeAddress || '',
      startFromHomeAddress: user?.startFromHomeAddress ?? false,
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPassword,
  } = useForm();

  const updateProfileMutation = useMutation(authAPI.updateProfile, {
    onSuccess: (response) => {
      updateUser(response.data.user);
      queryClient.invalidateQueries('auth');
      toast.success('Profile updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    },
  });

  const changePasswordMutation = useMutation(authAPI.changePassword, {
    onSuccess: () => {
      toast.success('Password changed successfully');
      setShowPasswordForm(false);
      resetPassword();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to change password');
    },
  });

  const onProfileSubmit = (data) => {
    updateProfileMutation.mutate({
      ...data,
      homeAddress: data.homeAddress?.trim() || null,
    });
  };

  const homeGeocoded = user?.homeLatitude != null && user?.homeLongitude != null;
  const homeAddressValue = watchProfile('homeAddress');
  const canStartFromHome = Boolean(homeAddressValue?.trim() || user?.homeAddress);

  const onPasswordSubmit = (data) => {
    changePasswordMutation.mutate(data);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your account information and preferences
        </p>
      </div>

      {/* Profile Information */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <User className="h-5 w-5 mr-2" />
            Personal Information
          </h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label">First Name</label>
                <input
                  {...registerProfile('firstName', { required: 'First name is required' })}
                  type="text"
                  className={`input ${profileErrors.firstName ? 'input-error' : ''}`}
                />
                {profileErrors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{profileErrors.firstName.message}</p>
                )}
              </div>
              <div>
                <label className="label">Last Name</label>
                <input
                  {...registerProfile('lastName', { required: 'Last name is required' })}
                  type="text"
                  className={`input ${profileErrors.lastName ? 'input-error' : ''}`}
                />
                {profileErrors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{profileErrors.lastName.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="label">Email</label>
              <div className="flex items-center">
                <Mail className="h-5 w-5 text-gray-400 mr-2" />
                <span className="text-sm text-gray-500">{user?.email}</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
            </div>

            <div>
              <label className="label">Phone</label>
              <input
                {...registerProfile('phone')}
                type="tel"
                className={`input ${profileErrors.phone ? 'input-error' : ''}`}
                placeholder="(555) 123-4567"
              />
              {profileErrors.phone && (
                <p className="mt-1 text-sm text-red-600">{profileErrors.phone.message}</p>
              )}
            </div>

            <div>
              <label className="label inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-gray-400" />
                Home address
              </label>
              <input
                {...registerProfile('homeAddress')}
                type="text"
                className="input"
                placeholder="456 Oak Ave, Jackson, MS"
              />
              <p className="mt-1 text-xs text-gray-500">
                {homeGeocoded
                  ? `Geocoded ✓ (${Number(user.homeLatitude).toFixed(4)}, ${Number(user.homeLongitude).toFixed(4)}).`
                  : 'Saved addresses are geocoded automatically when you save.'}
              </p>
              <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
                <input
                  {...registerProfile('startFromHomeAddress')}
                  type="checkbox"
                  disabled={!canStartFromHome}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                />
                Use my home address as my route start
              </label>
              <p className="mt-1 text-xs text-gray-500">
                When enabled, your daily route and auto-schedule assignments start from home instead of the shop.
              </p>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={updateProfileMutation.isLoading}
                className="btn btn-primary"
              >
                <Save className="h-4 w-4 mr-2" />
                {updateProfileMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Password Change */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Key className="h-5 w-5 mr-2" />
            Change Password
          </h3>
        </div>
        <div className="card-body">
          {!showPasswordForm ? (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="btn btn-secondary"
            >
              Change Password
            </button>
          ) : (
            <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
              <div>
                <label className="label">Current Password</label>
                <input
                  {...registerPassword('currentPassword', { required: 'Current password is required' })}
                  type="password"
                  className={`input ${passwordErrors.currentPassword ? 'input-error' : ''}`}
                />
                {passwordErrors.currentPassword && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword.message}</p>
                )}
              </div>

              <div>
                <label className="label">New Password</label>
                <input
                  {...registerPassword('newPassword', {
                    required: 'New password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters',
                    },
                  })}
                  type="password"
                  className={`input ${passwordErrors.newPassword ? 'input-error' : ''}`}
                />
                {passwordErrors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword.message}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    resetPassword();
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={changePasswordMutation.isLoading}
                  className="btn btn-primary"
                >
                  {changePasswordMutation.isLoading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
